/* eslint-disable max-len */

import {
  Snackbar, Alert, Button,
} from '@mui/material';
import { CommonModal, ErrorBar } from '@components';
import { useRef, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
import { uploadFile } from '@api';
import { logError } from '@log';
import CircularProgress from '@mui/material/CircularProgress';
import Cancel from '@assets/circle-cancel.svg';
import Camera from '@assets/photo_camera.png';
import classes from './reason-dialog.module.css';
import { compareFiles } from '../../utils';

const MAX_UPLOAD_FILE_SIZE = 20;
const MAX_UPLOAD_FILE_NUM = 10;
const ALLOWED_UPLOAD_TYPE = ['image/*', 'video/*'];

const MODAL_TITLE = {
  upload: '1. Upload supporting materials',
  remark: '2. Please provide a description of the failure and how this occurred in detail',
};
const FailedToastText = `Maximum upload file size: ${MAX_UPLOAD_FILE_SIZE}MB, ${MAX_UPLOAD_FILE_NUM} files at most.`;

export const ReasonDialog = ({
  open, reason = {}, handleClose, handleOK,
}) => {
  // const [reasonId, setReasonId] = useState(reason.reason);
  const [comments, setComments] = useState(reason.comment);
  // const [isFetching, setIsFetching] = useState(false);
  const reasonFiles = reason.files?.filter((file) => !file.isLoading);
  // 过滤了上次上传没有完成的文件
  const [files, setFiles] = useState(reasonFiles || []);
  const [showToast, setShowToast] = useState(false);
  const [toastText, setToastText] = useState('');
  const [showFailedToast, setShowFailedToast] = useState(false);
  const [failedToastText, setFailedToastText] = useState('');

  const inputValue = useRef(null);
  // 当前所有选择文件的总大小（单位：Byte）
  const currentSize = files.reduce((sumSize, curFile) => sumSize + curFile.size, 0);
  // const isLoading = !!files?.find((file) => file.isLoading);

  const onCloseToast = () => {
    setShowToast(false);
  };

  const onCloseFailedToast = () => {
    setShowFailedToast(false);
  };

  const upload = async (fileList, originFiles) => {
    try {
      if (!fileList.length) {
        return [];
      }
      const filePathList = await uploadFile([...fileList]);
      if (!filePathList) {
        return [];
      }
      const resultFiles = [];
      filePathList.forEach((path, index) => {
        resultFiles[index] = {
          id: originFiles[index].id,
          path,
        };
      });
      return resultFiles;
    } catch (error) {
      logError(error);
      return [];
    }
  };

  const handleChange = async (event) => {
    try {
      const fileList = event.target.files;
      if (!fileList?.length) {
        return;
      }
      // 限制上传文件数量
      if (files.length + fileList.length > MAX_UPLOAD_FILE_NUM) {
        setToastText(FailedToastText);
        setShowToast(true);
        inputValue.current.value = '';
        return;
      }
      // 限制上传文件大小
      const fileSize = Array.prototype.slice.call(fileList)
        .reduce((sumSize, curFile) => sumSize + curFile.size, 0);
      if (fileSize + currentSize > MAX_UPLOAD_FILE_SIZE * 1024 * 1024) {
        setToastText(FailedToastText);
        setShowToast(true);
        inputValue.current.value = '';
        return;
      }

      setShowToast(false);
      // 添加文件到文件列表
      const newFiles = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fileList.length; i++) {
        newFiles.push({
          id: Date.now() + fileList[i].name,
          originalFileName: fileList[i].name,
          path: '',
          isLoading: true,
        });
      }
      setFiles([...files, ...newFiles]);

      // 上传文件
      const resultList = await upload(fileList, newFiles);
      // 上传文件失败，从文件列表中删除刚上传的文件
      if (!resultList?.length) {
        setFiles([...files]);
        setFailedToastText(`Failed to upload ${newFiles.map((item) => item.originalFileName).join('; ')}. Please try again.`);
        setShowFailedToast(true);
        inputValue.current.value = '';
        return;
      }

      // 上传成功，更新文件列表
      setFiles((preFiles) => preFiles.map((file) => {
        const resultFile = resultList.find((item) => item.id === file.id);
        if (!resultFile) {
          return { ...file };
        }
        return { ...file, path: resultFile.path, isLoading: false };
      }));

      /** 防止第二次上传相同文件失效 */
      inputValue.current.value = '';
    } catch (error) {
      console.log(error);
      setFailedToastText('upload files error');
      setShowFailedToast(true);
    }
  };

  const deleteFile = (index) => {
    files.splice(index, 1);
    setFiles([...files]);
  };

  const onCommentsChange = (e) => {
    setComments(e.target.value);
  };
  const close = () => {
    handleClose();
  };

  // 上传完毕后，continue才能点击
  const currentFiles = files.filter((item) => !item.isLoading);
  // 弹窗未修改，continue按钮disabled
  const isDisabled = (!comments || currentFiles?.length < 2) || (reason.comment === comments && compareFiles(files, reasonFiles));
  return (
    <CommonModal
      open={open}
      title=" Tell us more about the issue"
      handleClose={close}
      continueDisabled={isDisabled}
      handleOK={() => handleOK({ comments, files })}
    >
      <div className={classes.content}>
        <div className={classes.subTitle}>{MODAL_TITLE.upload}</div>
        <div>
          Please upload at least 2 photos or a video showing your item failure.
        </div>
        <section className={classes.upload_content}>

          <Button
            component="label"
            style={{ textTransform: 'none' }}
            className={classes.upload_block}
          >
            <input
              ref={inputValue}
              hidden
              multiple
              accept={ALLOWED_UPLOAD_TYPE?.join(', ') || '*'}
              type="file"
              onChange={handleChange}
            />
            <section className={classes.upload_btn}>
              <img src={Camera} alt="" className={classes.camera_icon} />
              Add Files
            </section>
          </Button>

          <section className={classes.file_list}>
            {files.map((file, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <section className={classes.file} key={`${file.originalFileName}_${index}`}>
                <span className={classes.file_title}>
                  {file.originalFileName}
                </span>
                {file.isLoading
                  ? <CircularProgress size={12} className={classes.loading_btn} />
                  : (
                    <section className={classes.delete_btn} onClick={() => deleteFile(index)}>
                      <img
                        src={Cancel}
                        className={classes.cancel_icon}
                        alt=""
                      />
                    </section>
                  )}
              </section>
            ))}
          </section>
        </section>
        {/* 提示 toast */}
        {showToast ? (
          <ErrorBar className={classes.error} message={toastText} canClose onClose={onCloseToast} />
        ) : null}
        <div className={classes.divider}></div>
        <div className={classes.subTitle}>
          {MODAL_TITLE.remark}
        </div>
        <textarea
          rows={2}
          className={classes.input_comments}
          onChange={onCommentsChange}
          value={comments || ''}
          maxLength="300"
        >
        </textarea>
        <div className={classes.comments_tips}> Max 300 characters</div>
      </div>

      {showFailedToast ? (
        <Snackbar
          className={classes.bottom_toast}
          open={showFailedToast}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'bottom',
          }}
          autoHideDuration={3000}
          onClose={onCloseFailedToast}
        >
          <Alert
            icon={false}
            severity="error"
            className={classes.failed_toast}
            onClose={onCloseFailedToast}
          >
            {failedToastText}
          </Alert>
        </Snackbar>
      ) : null}
    </CommonModal>
  );
};
